import React, { useEffect, useContext, useState } from "react";
import AppContext from "../../AppContext";
import OutsideClickHandler from "react-outside-click-handler";
import useNotifications from "../../hooks/useNotifications";
import useUsers from "../../hooks/useUsers";

// import { ReactComponent as NotifiationSvg } from "../../assets/header/notification.svg";
import { ReactComponent as NotifiationSvg } from "../../assets/header/bell.svg";
import { ReactComponent as EmptyStateSvg } from "../../assets/notifications/empty.svg";

import "../../styles/notifications.scss";
import { format } from "date-fns";
import { langComponent } from "../../constants";
import { toast } from "react-toastify";
import { SocketContext } from "../../contexts/SocketContext";

const NotificationType = {
  commission: "commission",
  submission: "submission",
  invoice: "invoice",
  quotation: "quotation",
};

const NotificationActionType = {
  create: "create",
  update: "update",
  delete: "delete",
};

const NotificationItem = ({ index, item }) => {
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.notification);
  const { createdBy, action, type, createdOn } = item;

  const getAction = (actionType) => {
    if (actionType === NotificationActionType.create) {
      return Translation("created");
    }
    if (actionType === NotificationActionType.update) {
      return Translation("updated");
    }
    if (actionType === NotificationActionType.delete) {
      return Translation("deleted");
    }
    return actionType;
  };

  const getType = (notificationType) => {
    if (notificationType === NotificationType.commission) {
      return Translation("commission");
    }

    if (notificationType === NotificationType.submission) {
      return Translation("submission");
    }

    if (notificationType === NotificationType.invoice) {
      return Translation("invoice");
    }

    if (notificationType === NotificationType.quotation) {
      return Translation("a quotation");
    }

    return notificationType;
  };

  const _createdOn = new Date(createdOn);

  return (
    <>
      {index !== 0 && <div className="notifications-dropdown__separator" />}
      <div className="notifications-dropdown-item">
        <div className="notifications-dropdown-item-icon">
          <img
            className="notifications-dropdown-item-icon__image"
            src={createdBy.avatar}
            width={50}
          />
        </div>
        <div className="notifications-dropdown-item__name">
          {`${createdBy?.firstname || "none"} ${getAction(action)} ${getType(
            type
          )}`}
        </div>
        <div className="notifications-dropdown-item-date">
          {format(_createdOn, "dd MMM yy")}
          <br />
          {format(_createdOn, "HH:mm")}
        </div>
      </div>
    </>
  );
};

const Notification = ({ isPage, handleNavigateTo }) => {
  const { user, isAuth, getLang } = useContext(AppContext);
  const { queryNotification, readNotification } = useNotifications();
  const { queryUsers } = useUsers();
  const Translation = getLang(langComponent.notification);
  const { socket } = useContext(SocketContext);

  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState();
  const [toggle, setToggle] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    if (!isAuth) return;
    // socket.on("notification", handleNewNotification);
    getNotification();

    return () => {
      // socket.off("notification", handleNewNotification);
      setNotifications([]);
    };
  }, [isAuth]);

  useEffect(() => {
    if (limit === 10) return;
    getNotification();
  }, [limit]);

  const handleNewNotification = (data) => {
    const notification = JSON.parse(data);
    handleGetNotification(notification);
  };

  const getNotification = async () => {
    try {
      const { _id, plan, company } = user;
      const filter = [
        {
          users: { $in: [_id] },
        },
        {
          broadcast: true,
        },
      ];

      if (plan) {
        filter.push({
          plans: { $in: [plan] },
        });
      }

      if (company) {
        filter.push({
          companies: { $in: [company] },
        });
      }

      // * Get first 10 notifications
      const { data: notificationData } = await queryNotification(
        {
          createdBy: { $ne: _id },
          $or: filter,
        },
        1,
        limit,
        null,
        { createdOn: -1 }
      );

      // * Get count of all unread notifications
      const { data: unreadNotifications } = await queryNotification(
        {
          createdBy: { $ne: _id },
          "readBy.user": { $nin: [_id] },
          $or: filter,
        },
        null,
        -1,
        { _id: 1 }
      );

      setUnreadCount(unreadNotifications.length);

      if (notificationData.length === 0) {
        setNotifications([]);
        return;
      }

      const userId = notificationData.map((m) => {
        return m.createdBy;
      });

      const { data: users } = await queryUsers(
        { _id: { $in: userId } },
        null,
        null,
        {
          _id: 1,
          avatar: 1,
          firstname: 1,
          surname: 1,
        }
      );

      notificationData.map((m) => {
        const findUser = users.find((f) => f._id === m.createdBy);
        m.createdBy = findUser;
        m.isRead = m.readBy.find((f) => f.user === _id) ? true : false;
      });

      setNotifications(notificationData);
    } catch (error) {
      toast.error("Unable to get notifications");
    }
  };

  const handleGetNotification = async (notification) => {
    const { data: users } = await queryUsers(
      { _id: { $in: [notification.createdBy] } },
      null,
      null,
      {
        _id: 1,
        avatar: 1,
        firstname: 1,
        surname: 1,
      }
    );
    if (users.length !== 0) {
      notification.createdBy = users[0];
    }
    setNotifications((current) => [...current, notification]);
  };

  const markReadNotification = async (id, isRead) => {
    if (isRead) return;
    try {
      await readNotification(id);
    } catch (error) {
      toast.error("Unable to read notification");
    }
  };

  const markReadAllNotification = async () => {
    try {
      const readTemp = [];
      for (const notification of notifications) {
        readTemp.push(readNotification(notification._id));
      }
      await Promise.allSettled(readTemp);
      await getNotification();
    } catch (error) {
      toast.error("Unable to read notification");
    }
  };

  const handleClickToggle = () => {
    setToggle(!toggle);
  };

  const handleClickViewAll = () => {
    setToggle(false);
    handleNavigateTo("notification");
  };

  const handleSelectCurrentNotification = async (notificationId) => {
    const { _id } = currentNotification || {};
    if (_id === notificationId) return;

    const beforeNotification = notifications.find(
      (f) => f._id === notificationId
    );
    const { isRead } = beforeNotification;
    await markReadNotification(notificationId, isRead);
    if (!isRead) {
      await getNotification();
    }

    const afterNotification = notifications.find(
      (f) => f._id === notificationId
    );

    setCurrentNotification(afterNotification);
  };

  const handleLoadNotificationMore = () => {
    setLimit((current) => current + 10);
  };

  if (isPage) {
    return (
      <div className="notifications notifications--page">
        <h3 className="notifications__header">Notifications</h3>
        <div className="notifications-list">
          {unreadCount > 0 && (
            <div className="notifications-list-header">
              <div className="notifications-list-header__name">
                {Translation("Notification")}
              </div>
              <span className="notifications-list-header__total">
                {unreadCount}
              </span>
              <span style={{ flexGrow: 1 }}>
                {Translation("pending notifications")}
              </span>
              <span
                className="notifications-list-header__clear-button"
                onClick={() => markReadAllNotification()}
              >
                {Translation("Mark read all")}
              </span>
            </div>
          )}
          {notifications.length === 0 && (
            <div className="notifications-empty">
              <EmptyStateSvg />
              <span className="notifications-empty__desc">
                No notifications found
              </span>
            </div>
          )}
          {notifications.map((data, i) => {
            const { _id } = data;
            return (
              <div onClick={() => handleSelectCurrentNotification(_id)}>
                <NotificationItem key={i} item={data} index={i} />
              </div>
            );
          })}
          {notifications.length > 10 && (
            <div
              className="notifications-list-footer"
              onClick={handleLoadNotificationMore}
            >
              {Translation("Load more")}
            </div>
          )}
        </div>
        {currentNotification && (
          <div className="notifications-content">
            <div className="notifications-content-item">
              <div className="notifications-content-item-icon">
                <img
                  className="notifications-content-item-icon__image"
                  src={currentNotification.createdBy?.avatar}
                  width={50}
                />
              </div>
              <div className="notifications-content-item__name">
                {currentNotification.createdBy?.firstname}{" "}
                {currentNotification.createdBy?.surname}
              </div>
              <div className="notifications-content-item-date">
                {format(new Date(currentNotification.createdOn), "dd MMM yy")}{" "}
                {format(new Date(currentNotification.createdOn), "HH:mm")}
              </div>
              <div className="notifications-content-item__message">
                {currentNotification.action} {currentNotification.type} ID{" "}
                {currentNotification.id} DATA {currentNotification.msg}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="notifications">
      <div className="notifications-icon" onClick={handleClickToggle}>
        <NotifiationSvg className="notifications-icon__img" width={23} />
        {unreadCount > 0 && <div className="notifications-icon__alert" />}
      </div>
      {toggle && (
        <OutsideClickHandler onOutsideClick={() => setToggle(false)}>
          <div className="notifications-dropdown">
            {unreadCount > 0 && (
              <div className="notifications-dropdown-header">
                <span className="notifications-dropdown-header__total">
                  {unreadCount}
                </span>
                <span style={{ flexGrow: 1 }}>
                  {Translation("pending notifications")}
                </span>
                <span
                  className="notifications-dropdown-header__clear-button"
                  onClick={() => markReadAllNotification()}
                >
                  {Translation("Mark read all")}
                </span>
              </div>
            )}
            {notifications.length === 0 && (
              <div className="notifications-empty">
                <EmptyStateSvg className="notifications-dropdown__empty-state" />
                <span className="notifications-empty__desc">
                  No notifications found
                </span>
              </div>
            )}
            {notifications.map((data, i) => {
              const { _id } = data;
              return (
                <NotificationItem
                  key={i}
                  item={data}
                  index={i}
                  onClick={() => markReadNotification(_id)}
                />
              );
            })}
            <div
              className="notifications-dropdown-footer"
              onClick={handleClickViewAll}
            >
              {Translation("View all")}
            </div>
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

Notification.propTypes = {};

export default Notification;
